import { FC, useState, useEffect, useContext } from "react";
import moment from "moment";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { Link } from "react-router-dom";
import ProviderSetService from "@app/provider-sets/providerSetService";
import { ProviderSetVersionOverallSummaryModel, ProviderSetVersionModel } from "@app/provider-sets/providerSet";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { ProviderSetVersionValidationStatusEnum } from "@app/provider-sets/enumerations";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";

interface OverallSummaryProps {
  providerSetId?: string;
  version?: ProviderSetVersionModel;
  compareVersion: ProviderSetVersionModel;
  onUpdate: (totalRecords: number) => void;
}

export const OverallSummary: FC<React.PropsWithChildren<OverallSummaryProps>> = (props: OverallSummaryProps) => {
  const user = useContext(UserContext);

  const providerSetService = new ProviderSetService();

  const [overallSummary, setOverallSummary] = useState<ProviderSetVersionOverallSummaryModel>();
  const [overallSummaryCompare, setOverallSummaryCompare] = useState<ProviderSetVersionOverallSummaryModel>();

  const [hasPassedValidation, setHasPassedValidation] = useState(false);
  const [hasPassedValidationCompare, setHasPassedValidationCompare] = useState(false);

  useEffect(() => {

    setOverallSummary(null);
    if (!props.providerSetId || !props.version || !props.version.providerSetVersionId)
      return;

    providerSetService.getProviderSetVersionOverallSummary(props.providerSetId, props.version.providerSetVersionId.toString()).then((summary) => {
      setOverallSummary(summary);
      props.onUpdate(summary.totalAccessPoints);
    });

    if (user.roles.includes(Roles.PROVIDER_VALIDATION_USER))
      setHasPassedValidation(props.version.providerSetVersionValidationStatus == ProviderSetVersionValidationStatusEnum.Passed);

  }, [props.providerSetId, props.version]);

  useEffect(() => {

    setOverallSummaryCompare(null);
    if (!props.providerSetId || !props.compareVersion || !props.compareVersion.providerSetVersionId)
      return;

    providerSetService.getProviderSetVersionOverallSummary(props.providerSetId, props.compareVersion.providerSetVersionId.toString()).then((summary) => {
      setOverallSummaryCompare(summary);
    });

    if (user.roles.includes(Roles.PROVIDER_VALIDATION_USER))
      setHasPassedValidationCompare(props.compareVersion.providerSetVersionValidationStatus == ProviderSetVersionValidationStatusEnum.Passed);

  }, [props.providerSetId, props.compareVersion]);

  return (
    <div className="card shadow-none border-0">
      <div className="card-header border-0 gray-500-bg pt-3 pb-3 ps-3">
        <strong>
          {!props.compareVersion ?
            "Overall Summary" :
            <div className="row">
              <div className="col-6">
                {moment.utc(props.compareVersion.dateCreated).local().format("MMM DD, YYYY h:mm:ss A")} Overall Summary
              </div>
              <div className="col-6">
                {moment.utc(props.version.dateCreated).local().format("MMM DD, YYYY h:mm:ss A")} Overall Summary
              </div>
            </div>
          }
        </strong>
      </div>
      <div className="row mb-1 ms-2">
        {!!props.compareVersion ?
          <div className="col-6">
            {!!overallSummaryCompare ? (
              <>
                {hasPassedValidationCompare ? <p className="mb-0"><QesIcon iconName={FontAwesome.CheckSquare} isDecorative={true} color={"#00cd87"} /> Validation Status: Completed – No Issues Found </p> : <p></p>}
                <Link to={{ pathname: `/provider-sets/${props.providerSetId}/version/${props.compareVersion.providerSetVersionId}/providers` }}><strong>{`${overallSummaryCompare.uniqueProviders.toLocaleString("en-US")} unique providers at ${overallSummaryCompare.uniqueLocations.toLocaleString("en-US")} unique locations`}</strong></Link>
                <p className="mb-0">{`(${overallSummaryCompare.totalAccessPoints.toLocaleString("en-US")} total access points)`}</p>
              </>) : <LoadingSpinner />
            }
          </div> : null
        }
        <div className="col-6">
          {!!overallSummary ? (
            <>
              {hasPassedValidation ? <p className="mb-0"><QesIcon iconName={FontAwesome.CheckSquare} isDecorative={true} color={"#00cd87"} /> Validation Status: Completed – No Issues Found </p> : <p></p>}
              <Link to={{ pathname: `/provider-sets/${props.providerSetId}/version/${props.version.providerSetVersionId}/providers` }}><strong>{`${overallSummary.uniqueProviders.toLocaleString("en-US")} unique providers at ${overallSummary.uniqueLocations.toLocaleString("en-US")} unique locations`}</strong></Link>
              <p className="mb-0">{`(${overallSummary.totalAccessPoints.toLocaleString("en-US")} total access points)`}</p>
            </>) : <LoadingSpinner />
          }
        </div>
      </div>
    </div>
  );
}